// Back-in-stock notify — Klaviyo client-side back-in-stock subscribe.
//
// Progressive enhancement for snippets/ca-back-in-stock.liquid. The <ca-back-in-stock> element
// carries the Klaviyo public company id and the current variant id as data-attributes (the snippet
// re-renders per-variant with the product section, so data-variant-id always reflects the selected
// variant). We open the native <dialog>, gather an email (required) and an optional consent-gated
// mobile number, consent-gate the transmit, then POST to Klaviyo's CLIENT back-in-stock endpoint
// (public — no secret key). The catalog-variant id uses Klaviyo's Shopify integration format:
// `$shopify:::$default:::<variant_id>`.
//
// IMPORTANT: all listeners are DELEGATED on the element itself and every child node is queried
// lazily at use time. The product section is re-rendered via Horizon's morph on variant change (and
// on initial hydration), which swaps child nodes out from under us — caching child refs in
// connectedCallback orphaned the trigger's click handler (the button node got replaced), so the
// dialog never opened. Delegation on `this` survives morph because the listeners live on the parent.
//
// No-JS / failure: the trigger button does nothing without JS (there is no native Klaviyo endpoint),
// so nothing is mis-sent. In the theme editor we never hit Klaviyo — we just preview the success face.

import { consentApiReady, formatUsPhone, marketingConsentAllowed, normalizePhone } from '@theme/consent';
import { EMAIL_RE, inEditor, postClient, readErrors } from '@theme/klaviyo-client';

async function klaviyoBackInStock(company, variantId, channels, profileAttributes) {
  const body = {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels,
        profile: { data: { type: 'profile', attributes: profileAttributes } },
      },
      relationships: {
        variant: {
          data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
        },
      },
    },
  };

  const res = await postClient('back-in-stock-subscriptions', company, body);

  // The client endpoint returns 202 Accepted with an empty body on success.
  if (!res.ok) throw new Error((await readErrors(res)) || `Back-in-stock subscribe failed (${res.status})`);
}

// Subscribe the profile to a Klaviyo marketing LIST — this is what grows the list and (for SMS)
// grants the consent Klaviyo needs to actually deliver texts. Separate from the back-in-stock
// subscription above (the one-off restock alert). The CLIENT /subscriptions endpoint subscribes a
// channel by INCLUDING its identifier (consent is governed by the list's opt-in settings); it does
// NOT accept an inline `subscriptions` object on the profile (that 400s). So we include the email
// only when email-marketing was opted into, and the phone only when SMS was opted into. Best-effort:
// the caller does not fail the flow if this errors. `consents` = { email: bool, sms: bool }.
async function klaviyoSubscribeList(company, listId, { email, phone }, consents) {
  const profile = {};
  if (consents.email && email) profile.email = email;
  if (consents.sms && phone) profile.phone_number = phone;
  if (!Object.keys(profile).length) return;

  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Back in stock',
        profile: { data: { type: 'profile', attributes: profile } },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    },
  };

  const res = await postClient('subscriptions', company, body);
  if (!res.ok) throw new Error(`List subscribe failed (${res.status})`);
}

class CaBackInStock extends HTMLElement {
  // Lazy accessors — never cache child nodes (see the morph note in the file header).
  get dialog() {
    return this.querySelector('[data-bis-dialog]');
  }
  get form() {
    return this.querySelector('[data-bis-form]');
  }
  get emailInput() {
    return this.querySelector('input[name="email"]');
  }
  get phoneInput() {
    return this.querySelector('input[name="phone"]');
  }
  get smsConsent() {
    return this.querySelector('[data-bis-sms-consent]');
  }
  get emailConsent() {
    return this.querySelector('[data-bis-email-consent]');
  }

  connectedCallback() {
    // Listeners live on `this`, so they survive child-node morphs. Guard against re-binding if
    // connectedCallback fires again (the element can be moved/re-connected by the section render).
    if (this._wired) return;
    this._wired = true;
    this.allowSms = this.dataset.sms === 'true';

    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.addEventListener('input', (e) => {
      if (e.target.matches('input[name="phone"]')) {
        this.maskPhone(e.target);
        this.toggleSmsDisclaimer(e.target);
      }
      // As soon as the shopper starts fixing an invalid field, drop the error text + red outline.
      this.clearError();
    });
    // Ticking the box that was flagged clears the error immediately (don't wait for resubmit).
    this.addEventListener('change', (e) => {
      if (e.target.matches('input[type="checkbox"]')) this.clearError();
    });
  }

  maskPhone(input) {
    const value = input.value;
    if (value.trim().startsWith('+')) return; // allow raw international entry
    const formatted = formatUsPhone(value);
    if (formatted !== value) input.value = formatted;
  }

  // Progressive disclosure: reveal the SMS consent/disclaimer only once a mobile number is entered.
  toggleSmsDisclaimer(input) {
    const wrap = this.querySelector('[data-bis-sms-disclaimer]');
    if (wrap) wrap.hidden = input.value.trim().length === 0;
  }

  onClick(event) {
    if (event.target.closest('[data-bis-open]')) {
      event.preventDefault();
      this.open();
      return;
    }
    if (event.target.closest('[data-bis-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target.closest('[data-bis-submit]')) {
      event.preventDefault();
      this.handleSubmit(event);
      return;
    }
    // Backdrop click closes (the click lands on the <dialog> itself, not its children). stopPropagation
    // so a backdrop click doesn't also bubble to an outer Quick Add dialog and close the whole flow.
    if (event.target === this.dialog) {
      event.stopPropagation();
      this.close();
    }
  }

  /**
   * Fill the signed-in shopper's address on open rather than rendering it into the page, where
   * any third-party script could read it regardless of consent. Best-effort: on failure the field
   * stays empty and the shopper types it, which is the guest path anyway.
   */
  async prefillAccountEmail() {
    const input = this.emailInput;
    if (!input || !input.hasAttribute('data-account-email') || input.value.trim()) return;

    try {
      const res = await fetch('/apps/wishlist/identity', { credentials: 'same-origin' });
      if (!res.ok) return;
      const { email } = await res.json();
      if (email && !input.value.trim()) input.value = email;
    } catch {
      /* leave it empty */
    }
  }

  open() {
    const dialog = this.dialog;
    if (!dialog) return;
    // Remember the opener so focus returns to it on close — the browser's native restoration
    // after dialog.close() is unreliable in Safari/iOS, and losing focus drops the shopper to
    // the top of the page.
    this._trigger = this.querySelector('[data-bis-open]');
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    // Lock background scroll (this element doesn't extend DialogComponent, so the `scroll-lock`
    // attribute wouldn't apply). Restore to the prior value on close so we don't clobber a lock
    // already set by an outer modal (e.g. Quick Add).
    this._prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    void this.prefillAccountEmail();

    // If the browser is sending a privacy signal, surface the folded consent language up front so the
    // required opt-in the shopper is about to tick already reads as the explicit override (no dead-end,
    // no separate checkbox). Falls back to the standalone box only when no list/consent box exists.
    void consentApiReady.then(() => {
      if (!marketingConsentAllowed()) this.revealGpc();
    });

    const email = this.emailInput;
    window.setTimeout(() => {
      try {
        email?.focus({ preventScroll: true });
      } catch (_) {
        email?.focus();
      }
    }, 50);
  }

  close() {
    const dialog = this.dialog;
    if (dialog) {
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }
    document.body.style.overflow = this._prevBodyOverflow || '';
    this._trigger?.focus({ preventScroll: true });
  }

  // Escape + Tab are handled manually so this dialog behaves correctly when nested inside another
  // open <dialog> (Quick Add opens the whole product-details column, incl. this notify dialog, in
  // its own modal). Native containment fails for nested dialogs: without stopPropagation, Escape
  // bubbles to the outer DialogComponent and closes the whole Quick Add flow. See the
  // nested-dialog-in-cart-drawer gotcha (same fix as cart-upsell / gift-wrapping).
  handleKeydown(event) {
    const dialog = this.dialog;
    if (!dialog || !dialog.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    // Enter in a notify input submits (the container isn't a <form>, so there's no native submit).
    if (event.key === 'Enter' && event.target.matches('input') && event.target.closest('[data-bis-form]')) {
      event.preventDefault();
      this.handleSubmit(event);
      return;
    }
    if (event.key !== 'Tab') return;

    const focusables = Array.from(
      dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.disabled && !el.hidden && el.offsetParent !== null);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  showError(message) {
    this.classList.remove('is-loading');
    const errorEl = this.querySelector('[data-bis-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  clearError() {
    const errorEl = this.querySelector('[data-bis-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    this.querySelectorAll('[aria-invalid="true"]').forEach((el) => el.removeAttribute('aria-invalid'));
    this.querySelectorAll('.is-attn').forEach((el) => el.classList.remove('is-attn'));
  }

  // Reveal the folded privacy-signal (GPC) language on the existing consent labels; fall back to the
  // standalone box when there's no consent label to carry it (no-list config).
  revealGpc() {
    this.classList.add('is-gpc');
    this.querySelectorAll('[data-bis-gpc-note]').forEach((n) => {
      n.hidden = false;
    });
    const standalone = this.querySelector('[data-bis-gpc]');
    if (standalone) standalone.hidden = false;
  }

  // Flag the offending field for assistive tech, draw the eye to the exact control (#2 — outline +
  // pulse on its label), scroll it into view, then surface the message.
  invalidate(el, message) {
    if (el) {
      el.setAttribute('aria-invalid', 'true');
      const wrap = el.closest('.ca-bis__opt, .ca-bis__consent-check, .ca-bis__field') || el;
      wrap.classList.add('is-attn');
      try {
        wrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } catch (_) {
        /* older browsers — focus() below still brings it into view */
      }
    }
    this.showError(message);
    el?.focus();
  }

  showSuccess() {
    this.classList.remove('is-loading');
    this.classList.add('is-success');
    const formWrap = this.querySelector('[data-bis-form-wrap]');
    const successEl = this.querySelector('[data-bis-success]');
    if (formWrap) formWrap.hidden = true;
    if (successEl) {
      successEl.hidden = false;
      window.setTimeout(() => {
        try {
          successEl.focus({ preventScroll: true });
        } catch (_) {
          successEl.focus();
        }
      }, 60);
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.classList.contains('is-loading')) return;
    this.clearError();

    const d = this.dataset;
    const emailInput = this.emailInput;
    const phoneInput = this.phoneInput;
    const smsConsent = this.smsConsent;

    // Email is required.
    const email = ((emailInput && emailInput.value) || '').trim();
    if (!EMAIL_RE.test(email)) {
      this.invalidate(emailInput, d.errEmail || 'Please enter a valid email address.');
      return;
    }

    // Email opt-in is REQUIRED when the checkbox is present (i.e. a marketing List ID is configured).
    // Checking it is what subscribes the profile to the list — which grows the list and guarantees
    // the restock email delivers, including to previously-unsubscribed (suppressed) profiles.
    const emailConsentEl = this.emailConsent;
    if (emailConsentEl && !emailConsentEl.checked) {
      this.invalidate(emailConsentEl, d.errEmailConsent || 'Please check the box so we can email you when it’s back.');
      return;
    }

    // SMS is an optional add-on. If a mobile number is entered it REQUIRES explicit consent (TCPA);
    // a checked box with no/invalid number is also a user error.
    let smsPhone = null;
    const phoneRaw = ((phoneInput && phoneInput.value) || '').trim();
    if (this.allowSms && phoneRaw.length > 0) {
      if (!(smsConsent && smsConsent.checked)) {
        this.invalidate(smsConsent, d.errSmsConsent || 'Please check the box to agree to receive texts.');
        return;
      }
      const phone = normalizePhone(phoneRaw);
      if (!phone) {
        this.invalidate(phoneInput, d.errPhone || 'Please enter a valid mobile number.');
        return;
      }
      smsPhone = phone;
    }

    // Theme editor: never hit Klaviyo — just preview the success face.
    if (inEditor()) {
      this.showSuccess();
      return;
    }

    const company = (d.klCompany || '').trim();
    const variantId = (d.variantId || '').trim();
    const listId = (d.klList || '').trim();
    if (!company || !variantId || !listId) {
      this.showError(d.errUnconfigured || 'Notifications aren’t connected yet. Please try again later.');
      return;
    }

    // Privacy-signal gate. Rather than dead-end a GPC/opt-out visitor, the folded consent language is
    // shown on the existing opt-in boxes (revealed in open()). Any checked consent box — the required
    // email opt-in, the SMS opt-in, or the standalone fallback — is the explicit, purpose-specific
    // override. In the configured (list) case the required email box is already ticked here, so this
    // never blocks; it only guards the no-consent-box fallback.
    await consentApiReady;
    if (!marketingConsentAllowed()) {
      const gpcConsent = this.querySelector('[data-bis-gpc-consent]');
      const consentGiven = [emailConsentEl, this.smsConsent, gpcConsent].some((c) => c && c.checked);
      if (!consentGiven) {
        this.revealGpc();
        this.invalidate(
          gpcConsent || emailConsentEl || this.smsConsent,
          d.errConsent || 'Almost there! Just check the box to finish signing up.'
        );
        return;
      }
    }

    const consents = {
      email: !!(this.emailConsent && this.emailConsent.checked),
      sms: !!smsPhone,
    };

    this.classList.add('is-loading');
    try {
      const bisChannels = smsPhone ? ['EMAIL', 'SMS'] : ['EMAIL'];
      const bisProfile = smsPhone ? { email, phone_number: smsPhone } : { email };
      try {
        await klaviyoBackInStock(company, variantId, bisChannels, bisProfile);
      } catch (bisErr) {
        if (smsPhone) {
          await klaviyoBackInStock(company, variantId, ['EMAIL'], { email });
        } else {
          throw bisErr;
        }
      }

      if (listId && (consents.email || consents.sms)) {
        try {
          await klaviyoSubscribeList(company, listId, { email, phone: smsPhone }, consents);
        } catch (subErr) {
          if (window.console) console.warn('[ca-back-in-stock] list subscribe', subErr);
          if (consents.email) throw subErr;
        }
      }
      this.showSuccess();
    } catch (error) {
      this.showError(d.errGeneric || 'Something went wrong. Please try again.');
      if (window.console) console.warn('[ca-back-in-stock]', error);
    }
  }
}

if (!customElements.get('ca-back-in-stock')) {
  customElements.define('ca-back-in-stock', CaBackInStock);
}
